// shared/pdf/documents/BalanceSheetDocument.jsx
import React from 'react';
import { Document, Page, View, Text, StyleSheet } from '@react-pdf/renderer';
import { PDF_THEMES, DEFAULT_THEME } from '../themes';
import PdfHeader from '../components/PdfHeader';
import PdfFooter from '../components/PdfFooter';
import { fmtINR } from '../pdfUtils';

// ── Item types used in the flat row list ─────────────────────────────────────
// section-head | sg-head | account | account-special | sub-total | filler

function buildLeftItems(data) {
  const surplus     = Number(data?.incomeExpense?.surplus ?? 0);
  const equityRows  = data?.equity?.rows || [];
  const liabilityGroups = data?.liabilities?.subGroups || [];

  const items = [];

  // ── Liabilities ──────────────────────────────────────────────────────────
  items.push({ type: 'section-head', label: 'Liabilities' });

  if (liabilityGroups.length === 0) {
    items.push({ type: 'filler', label: 'No liabilities', muted: true });
  } else {
    liabilityGroups.forEach((sg) => {
      items.push({ type: 'sg-head', label: sg.name });
      (sg.rows || []).forEach((acc) =>
        items.push({
          type: 'account',
          label: acc.name || '',
          amount: acc.balance ?? 0,
        })
      );
      if ((sg.rows || []).length === 0)
        items.push({ type: 'filler', label: 'No accounts', muted: true });
      if ((sg.rows || []).length > 1)
        items.push({ type: 'sub-total', label: `Sub-Total – ${sg.name}`, amount: sg.subtotal ?? 0 });
    });
  }

  // ── Equity & Corpus ───────────────────────────────────────────────────────
  items.push({ type: 'section-head', label: 'Equity & Corpus' });

  if (equityRows.length === 0 && surplus === 0) {
    items.push({ type: 'filler', label: 'No equity entries', muted: true });
  } else {
    equityRows.forEach((acc) =>
      items.push({
        type: 'account',
        label: acc.name || '',
        amount: acc.balance ?? 0,
      })
    );
    if (surplus !== 0)
      items.push({
        type: 'account-special',
        label: 'Net Income for Period',
        amount: surplus,
        color: surplus >= 0 ? '#059669' : '#DC2626',
      });
  }

  return items;
}

function buildRightItems(data) {
  const assetGroups = data?.assets?.subGroups || [];
  const items = [];

  items.push({ type: 'section-head', label: 'Assets' });

  if (assetGroups.length === 0) {
    items.push({ type: 'filler', label: 'No assets', muted: true });
  } else {
    assetGroups.forEach((sg) => {
      items.push({ type: 'sg-head', label: sg.name });
      (sg.rows || []).forEach((acc) =>
        items.push({
          type: 'account',
          label: acc.name || '',
          amount: acc.balance ?? 0,
        })
      );
      if ((sg.rows || []).length === 0)
        items.push({ type: 'filler', label: 'No accounts', muted: true });
      if ((sg.rows || []).length > 1)
        items.push({ type: 'sub-total', label: `Sub-Total – ${sg.name}`, amount: sg.subtotal ?? 0 });
    });
  }

  return items;
}

// ── Styles ───────────────────────────────────────────────────────────────────
const s = StyleSheet.create({
  page:    { fontFamily: 'NotoSans', paddingBottom: 60 },
  content: { marginHorizontal: 20, marginTop: 10 },
  outer:   { borderWidth: 1, borderStyle: 'solid' },

  // Each paired row is a flex row; left cell gets a right border
  pairRow:   { flexDirection: 'row' },
  cellLeft:  { flex: 1, borderRightWidth: 1, borderRightStyle: 'solid' },
  cellRight: { flex: 1 },

  // Column header (very top of each side)
  colHead: {
    flexDirection: 'row', paddingHorizontal: 6, paddingVertical: 5,
    borderBottomWidth: 1, borderBottomStyle: 'solid',
  },

  // Section header ("LIABILITIES", "EQUITY & CORPUS", "ASSETS")
  secHead: {
    flexDirection: 'row', paddingHorizontal: 6, paddingVertical: 4,
    borderTopWidth: 0.5, borderTopStyle: 'solid',
    borderBottomWidth: 0.5, borderBottomStyle: 'solid',
  },

  // Sub-group name row
  sgHead: { flexDirection: 'row', paddingHorizontal: 6, paddingVertical: 3 },

  // Account / special / sub-total rows
  accRow: { flexDirection: 'row', paddingLeft: 16, paddingRight: 6, paddingVertical: 3, minHeight: 15 },
  stRow:  { flexDirection: 'row', paddingLeft: 16, paddingRight: 6, paddingVertical: 3 },

  // Filler (empty body row)
  fillerRow: { flexDirection: 'row', paddingLeft: 16, paddingRight: 6, paddingVertical: 3, minHeight: 14 },

  // Section total (e.g. "Total Equity & Corpus" / "Total Assets")
  secTotalRow: {
    flexDirection: 'row', paddingHorizontal: 6, paddingVertical: 4,
    borderTopWidth: 0.5, borderTopStyle: 'solid',
  },

  // Grand total
  grandRow: {
    flexDirection: 'row', paddingHorizontal: 6, paddingVertical: 5,
    borderTopWidth: 2, borderTopStyle: 'solid',
  },

  balWrap: { alignItems: 'center', marginTop: 8 },
});

// ── Cell content renderers (return a single View) ────────────────────────────
function renderCellContent(item, rowIndex, t) {
  if (!item) {
    return <View style={[s.fillerRow, { backgroundColor: rowIndex % 2 === 0 ? t.white : t.rowAlt }]} />;
  }

  switch (item.type) {
    case 'section-head':
      return (
        <View style={[s.secHead, { backgroundColor: t.light, borderTopColor: t.borderMid, borderBottomColor: t.borderLight }]}>
          <Text style={{ fontSize: 8.5, fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 0.4, color: t.body }}>
            {item.label}
          </Text>
        </View>
      );

    case 'sg-head':
      return (
        <View style={[s.sgHead, { backgroundColor: t.sectionBg }]}>
          <Text style={{ fontSize: 8, fontWeight: 'bold', flex: 1, color: t.body }}>{item.label}</Text>
          {item.amount != null && (
            <Text style={{ fontSize: 8, fontWeight: 'bold', color: t.body }}>{fmtINR(item.amount)}</Text>
          )}
        </View>
      );

    case 'account': {
      const bg = rowIndex % 2 === 0 ? t.white : t.rowAlt;
      return (
        <View style={[s.accRow, { backgroundColor: bg }]} wrap={false}>
          <Text style={{ fontSize: 8, flex: 1, color: t.body }}>{item.label}</Text>
          <Text style={{ fontSize: 8, color: t.body }}>{fmtINR(item.amount)}</Text>
        </View>
      );
    }

    case 'account-special': {
      const bg = rowIndex % 2 === 0 ? t.white : t.rowAlt;
      const col = item.color === '#059669' ? t.statusGreen : item.color === '#DC2626' ? t.statusRed : item.color || t.body;
      return (
        <View style={[s.accRow, { backgroundColor: bg }]} wrap={false}>
          <Text style={{ fontSize: 8, flex: 1, color: col }}>{item.label}</Text>
          <Text style={{ fontSize: 8, color: col }}>{fmtINR(item.amount)}</Text>
        </View>
      );
    }

    case 'sub-total':
      return (
        <View style={[s.stRow, { backgroundColor: t.subtotalBg }]} wrap={false}>
          <Text style={{ fontSize: 7.5, flex: 1, color: t.textSub }}>{item.label}</Text>
          <Text style={{ fontSize: 7.5, color: t.textSub }}>{fmtINR(item.amount)}</Text>
        </View>
      );

    case 'filler':
    default:
      return (
        <View style={[s.fillerRow, { backgroundColor: rowIndex % 2 === 0 ? t.white : t.rowAlt }]}>
          {item.muted && (
            <Text style={{ fontSize: 7.5, color: t.textFaint }}>{item.label || ''}</Text>
          )}
        </View>
      );
  }
}

// ── Main document ─────────────────────────────────────────────────────────────
export default function BalanceSheetDocument({ data, theme: themeKey, orientation, sections }) {
  const t = PDF_THEMES[themeKey] || PDF_THEMES[DEFAULT_THEME];

  const surplus     = Number(data?.incomeExpense?.surplus ?? 0);
  const equityTotal = (data?.equity?.total ?? 0) + surplus;
  const liabTotal   = data?.liabilities?.total ?? 0;
  const assetTotal  = data?.assets?.total ?? 0;
  const grandLeft   = data?.totalLiabilitiesEquity ?? 0;
  const grandRight  = assetTotal;

  const leftItems  = buildLeftItems(data);
  const rightItems = buildRightItems(data);

  // Pad both to the same length so footer rows align perfectly
  const maxLen = Math.max(leftItems.length, rightItems.length);
  while (leftItems.length  < maxLen) leftItems.push(null);
  while (rightItems.length < maxLen) rightItems.push(null);

  return (
    <Document>
      <Page size="A4" orientation={orientation || 'portrait'} style={[s.page, { backgroundColor: t.white }]}>
        {sections.header && <PdfHeader derasar={data.derasar} reportTitle="Balance Sheet" theme={t} />}

        <View style={s.content}>
          <View style={[s.outer, { borderColor: t.borderMid }]}>

            {/* Column headers */}
            <View style={s.pairRow}>
              <View style={[s.cellLeft, s.colHead, { backgroundColor: t.dark, borderBottomColor: t.borderMid, borderRightColor: t.borderMid }]}>
                <Text style={{ fontSize: 8, fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 0.5, flex: 1, color: t.headerText }}>
                  Liabilities & Equity
                </Text>
                <Text style={{ fontSize: 7.5, fontWeight: 'bold', textTransform: 'uppercase', color: t.headerText }}>
                  Amt. in Rs.
                </Text>
              </View>
              <View style={[s.cellRight, s.colHead, { backgroundColor: t.dark, borderBottomColor: t.borderMid }]}>
                <Text style={{ fontSize: 8, fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 0.5, flex: 1, color: t.headerText }}>
                  Assets
                </Text>
                <Text style={{ fontSize: 7.5, fontWeight: 'bold', textTransform: 'uppercase', color: t.headerText }}>
                  Amt. in Rs.
                </Text>
              </View>
            </View>

            {/* Content rows — paired so they always align */}
            {Array.from({ length: maxLen }).map((_, i) => (
              <View key={i} style={s.pairRow} wrap={false}>
                <View style={[s.cellLeft, { borderRightColor: t.borderMid }]}>
                  {renderCellContent(leftItems[i], i, t)}
                </View>
                <View style={s.cellRight}>
                  {renderCellContent(rightItems[i], i, t)}
                </View>
              </View>
            ))}

            {/* Footer row 1 — "Total Equity & Corpus" aligned with "Total Assets" */}
            <View style={s.pairRow}>
              <View style={[s.cellLeft, s.secTotalRow, { backgroundColor: t.totalBg, borderTopColor: t.borderMid, borderRightColor: t.borderMid }]}>
                <Text style={{ fontSize: 8, fontWeight: 'bold', flex: 1, color: t.body }}>
                  Total Equity &amp; Corpus
                </Text>
                <Text style={{ fontSize: 8, fontWeight: 'bold', color: t.body }}>{fmtINR(equityTotal)}</Text>
              </View>
              <View style={[s.cellRight, s.secTotalRow, { backgroundColor: t.totalBg, borderTopColor: t.borderMid }]}>
                <Text style={{ fontSize: 8, fontWeight: 'bold', flex: 1, color: t.body }}>Total Assets</Text>
                <Text style={{ fontSize: 8, fontWeight: 'bold', color: t.body }}>{fmtINR(assetTotal)}</Text>
              </View>
            </View>

            {/* Footer row 2 — Grand Total aligned on both sides */}
            <View style={s.pairRow}>
              <View style={[s.cellLeft, s.grandRow, { backgroundColor: t.mid, borderTopColor: t.borderDark, borderRightColor: t.borderMid }]}>
                <Text style={{ fontSize: 9, fontWeight: 'bold', flex: 1, color: t.body }}>Grand Total</Text>
                <Text style={{ fontSize: 9, fontWeight: 'bold', color: t.body }}>{fmtINR(grandLeft)}</Text>
              </View>
              <View style={[s.cellRight, s.grandRow, { backgroundColor: t.mid, borderTopColor: t.borderDark }]}>
                <Text style={{ fontSize: 9, fontWeight: 'bold', flex: 1, color: t.body }}>Grand Total</Text>
                <Text style={{ fontSize: 9, fontWeight: 'bold', color: t.body }}>{fmtINR(grandRight)}</Text>
              </View>
            </View>

          </View>

          {/* Balance status */}
          {sections.summaryCards && (
            <View style={s.balWrap}>
              <Text style={{ fontSize: 8.5, fontWeight: 'bold', color: data?.isBalanced ? t.statusGreen : t.statusRed }}>
                {data?.isBalanced
                  ? 'Liabilities + Equity = Assets  ✓'
                  : `Out of Balance — Difference: ${fmtINR(Math.abs(grandRight - grandLeft))}`}
              </Text>
            </View>
          )}
        </View>

        {sections.footer && <PdfFooter theme={t} />}
      </Page>
    </Document>
  );
}



